import { IconChevronRight } from "@tabler/icons-react";
import { createLink } from "@tanstack/react-router";
import type * as React from "react";
import { forwardRef } from "react";
import { cn } from "@/lib/utils";

/**
 * Overview card shell (Paper "Card · …" frames): raised surface, card radius and
 * clipped corners so full-bleed table rows and footers follow the rounded edge.
 * It is also the `@container` that the overview tables narrow against.
 */
export function DashboardCard({ className, ...props }: React.ComponentProps<"section">) {
  return (
    <section
      data-slot="dashboard-card"
      className={cn(
        "@container flex min-w-0 flex-col overflow-hidden rounded-lg bg-surface text-ink shadow-[var(--paper-raised-shadow)]",
        className,
      )}
      {...props}
    />
  );
}

interface DashboardCardHeaderProps {
  title: React.ReactNode;
  titleId?: string;
  /** Leading mark before the title, e.g. the AI mark. */
  leading?: React.ReactNode;
  /** Muted text or legend that follows the title on the same baseline. */
  meta?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}

export function DashboardCardHeader({
  title,
  titleId,
  leading,
  meta,
  action,
  className,
}: DashboardCardHeaderProps) {
  return (
    <header
      className={cn("flex min-h-12 items-center justify-between gap-3 px-5 py-3.5", className)}
    >
      <div className="flex min-w-0 items-center gap-2">
        {leading}
        <div className="flex min-w-0 items-baseline gap-2">
          <h2 id={titleId} className="shrink-0 text-base font-semibold text-ink">
            {title}
          </h2>
          {meta ? (
            <div className="min-w-0 truncate text-xs text-muted-foreground">{meta}</div>
          ) : null}
        </div>
      </div>
      {action ? <div className="flex shrink-0 items-center">{action}</div> : null}
    </header>
  );
}

const CardLinkAnchor = forwardRef<HTMLAnchorElement, React.ComponentProps<"a">>(
  function CardLinkAnchor({ className, children, ...props }, ref) {
    return (
      <a
        ref={ref}
        className={cn(
          "-mx-1 inline-flex items-center gap-0.5 rounded-xs px-1 text-sm font-medium tabular-nums text-text-secondary outline-none transition-colors hover:text-ink focus-visible:ring-2 focus-visible:ring-focus-ring",
          className,
        )}
        {...props}
      >
        {children}
        <IconChevronRight
          aria-hidden
          className="size-3.5 shrink-0 text-muted-foreground"
          strokeWidth={1.75}
        />
      </a>
    );
  },
);

/** Header link ("View all 42 ›", "AI Operations ›"): text-sm label + 14px chevron. */
export const DashboardCardLink = createLink(CardLinkAnchor);

// HACK: the AdPraxis AI mark has no design token yet (Paper uses the same raw
// gradient); keep it in one place until it becomes a theme variable.
const AI_MARK_GRADIENT =
  "radial-gradient(circle farthest-corner at 25% 25% in oklab, #80EBFF 0%, #80EBFF00 55%), radial-gradient(circle farthest-corner at 80% 85% in oklab, #FF6FA8 0%, #FF6FA800 60%), linear-gradient(in oklab 135deg, #306BFF 0%, #5A3CF2 100%)";

const AI_MARK_SIZES = { sm: "size-2.5", md: "size-5" } as const;

/** Gradient orb that marks AdPraxis AI (20px in card headers, 10px as a legend / row dot). */
export function AiMark({
  size = "md",
  className,
}: {
  size?: keyof typeof AI_MARK_SIZES;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn("inline-block shrink-0 rounded-full", AI_MARK_SIZES[size], className)}
      style={{ backgroundImage: AI_MARK_GRADIENT }}
    />
  );
}

/** Two-letter marketplace chip ("US", "JP") used next to shop names and ASINs. */
